import { api } from "../../api";
import { getSearchParams } from "../../../utils/request";

/**
 * Chat reference search results returned to the UI.
 *
 * We store a compact payload so we can insert a stable token into messages:
 * `@[type:id|label]`
 *
 * @typedef {{ type: string, id: string, label: string, to: string }} ChatReference
 */

function safeId(item) {
  return String(item?._id || item?.id || "");
}

function last6(id) {
  const s = String(id || "");
  return s.length > 6 ? s.slice(-6) : s;
}

function formatYmd(value) {
  try {
    if (!value) return "";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "";
    return d.toISOString().slice(0, 10);
  } catch {
    return "";
  }
}

function buildLabel(type, item) {
  if (!item) return `${type}`;
  switch (type) {
    case "job":
      // Human-facing identifier for jobs
      return item.loadNumber || item.jobId || `Job ${last6(item._id)}`;
    case "manifest":
      // Manifest uses `manifestId` as the human-readable ID field
      if (item.manifestId) return item.manifestId;
      // Backward-compatible fallback: mimic the server's manifest naming pattern
      // (DRIVERNAME-YYYY-MM-DD-YYYY-MM-DD) without exposing Mongo _id.
      {
        const driverName =
          item?.driver?.fullName ||
          item?.driver?.firstName ||
          item?.drivers?.[0]?.fullName ||
          "";
        const start = formatYmd(item?.startDate);
        const end = formatYmd(item?.endDate);
        const parts = [driverName, start, end].filter(Boolean);
        if (parts.length >= 2) return parts.join("-");
      }
      return item.name || "Manifest";
    case "invoice":
      // Invoice uses `invoiceId` as the human-readable ID field
      return item.invoiceId || `Invoice ${last6(item._id)}`;
    case "payout":
      // Payout uses `payoutId` as the human-readable ID field
      if (item.payoutId) return item.payoutId;
      // Backward-compatible fallback: mimic the server's payout naming pattern
      // (DRIVERNAME(YYYY-MM-DD - YYYY-MM-DD)) without exposing Mongo _id.
      {
        const driverName =
          item?.driver?.fullName ||
          item?.employee?.fullName ||
          item?.drivers?.[0]?.fullName ||
          "";
        const start = formatYmd(item?.startDate);
        const end = formatYmd(item?.endDate);
        if (driverName && (start || end)) {
          const range = [start, end].filter(Boolean).join(" - ");
          return `${driverName}${range ? `(${range})` : ""}`;
        }
      }
      return "Payout";
    case "leg":
      // Human-facing identifier for legs (orderNumber or loadNumber)
      return item.orderNumber || item.loadNumber || `Leg ${last6(item._id)}`;
    case "equipment":
      return (
        item.serialNumber ||
        item.unitNumber ||
        item.name ||
        `Equipment ${last6(item._id)}`
      );
    case "client":
      return item.name || item.companyName || `Client ${last6(item._id)}`;
    case "user":
      return item.fullName || item.email || `User ${last6(item._id)}`;
    default:
      return item.name || item.title || `${type} ${last6(item._id)}`;
  }
}

function buildTo(type, id) {
  switch (type) {
    case "job":
      return `/jobs/${id}`;
    case "manifest":
      return `/manifests/${id}`;
    case "invoice":
      return `/invoices/${id}`;
    case "payout":
      return `/payouts/${id}`;
    case "equipment":
      return `/equipment/${id}`;
    case "client":
      return `/clients/${id}`;
    case "user":
      return `/users/${id}`;
    case "leg": {
      // id is encoded as "{jobId}:{legId}"
      const [jobId, legId] = String(id || "").split(":");
      if (jobId && legId) return `/jobs/${jobId}#leg-${legId}`;
      return "/";
    }
    default:
      return "/";
  }
}

function extractList(type, resp) {
  if (!resp) return [];
  // Many endpoints use mongoose-paginate and return `{ docs: [...] }`
  // (or nested under `{ data: { docs: [...] } }`). Handle those shapes first.
  if (Array.isArray(resp.docs)) return resp.docs;
  // Most endpoints follow `{ <plural>: [] }` shape
  const map = {
    job: "jobs",
    manifest: "manifests",
    invoice: "invoices",
    payout: "payouts",
    equipment: "equipment",
    client: "clients",
    user: "users",
  };
  const key = map[type];
  // Handle nested `{ data: { <plural>: [] } }` and direct `{ <plural>: [] }`
  const direct = resp[key];
  if (Array.isArray(direct)) return direct;

  const data = resp.data;
  if (data && typeof data === "object" && Array.isArray(data.docs)) return data.docs;
  if (Array.isArray(data)) return data;
  if (data && typeof data === "object" && Array.isArray(data[key])) return data[key];

  return [];
}

/**
 * Determine which reference types are relevant for the current appType.
 * @param {string} appType
 */
function getTypesForAppType(appType) {
  // Default to a broad set. Then narrow for app-specific screens.
  if (appType === "trucking") {
    return ["job", "leg", "manifest", "invoice", "payout", "equipment", "client", "user"];
  }
  if (appType === "serviceRepair") {
    return ["job", "invoice", "equipment", "client", "user"];
  }
  return ["job", "invoice", "equipment", "client", "user"];
}

const chatReferencesApi = api.injectEndpoints({
  endpoints: (builder) => ({
    /**
     * Search referenceable entities for chat mentions.
     *
     * @param {{ q: string, appType?: string, limitPerType?: number }} params
     */
    searchChatReferences: builder.query({
      async queryFn(
        { q, appType, limitPerType = 5, type, mode = "search" },
        _queryApi,
        _extraOptions,
        fetchWithBQ
      ) {
        const term = String(q || "").trim();

        const types = type ? [type] : getTypesForAppType(appType);

        // Recent mode: fetch recent items without searchTerm
        const baseParams =
          mode === "recent"
            ? {
                page: 1,
                limit: limitPerType,
                legacy: false,
              }
            : {
                page: 1,
                limit: limitPerType,
                legacy: false,
                search: true,
                searchTerm: term,
              };

        if (mode !== "recent" && (!term || term.length < 2)) {
          return { data: { results: [] } };
        }

        const requests = types.map(async (type) => {
          let url = "";
          if (type === "job") url = `jobs?${getSearchParams(baseParams)}`;
          if (type === "leg") url = `legs?${new URLSearchParams(baseParams)}`;
          if (type === "manifest") url = `manifests?${new URLSearchParams(baseParams)}`;
          if (type === "invoice") url = `invoices?${new URLSearchParams(baseParams)}`;
          if (type === "payout") url = `payouts?${new URLSearchParams(baseParams)}`;
          if (type === "equipment") url = `equipment?${getSearchParams(baseParams)}`;
          if (type === "client") url = `clients?${getSearchParams(baseParams)}`;
          if (type === "user") {
            const userParams = {
              legacy: false,
              page: 1,
              limit: limitPerType,
              ...(mode === "recent"
                ? {}
                : { search: true, model: "User", searchTerm: term }),
            };
            url = `users?${new URLSearchParams(userParams)}`;
          }

          const resp = await fetchWithBQ(url);
          if (resp.error) return { type, items: [] };
          return { type, items: extractList(type, resp.data) };
        });

        const settled = await Promise.all(requests);

        /** @type {ChatReference[]} */
        const results = [];

        settled.forEach(({ type, items }) => {
          (items || []).forEach((item) => {
            // For leg links, encode "{jobId}:{legId}" so we can route to the job+hash.
            if (type === "leg") {
              const legId = safeId(item);
              const jobId = safeId(item?.jobId);
              if (!legId || !jobId) return;
              // If searching, only keep legs whose orderNumber matches the user term
              if (mode !== "recent" && term) {
                const orderNumber = String(item?.orderNumber || "");
                if (!orderNumber.toLowerCase().includes(term.toLowerCase())) return;
              }
              const encodedId = `${jobId}:${legId}`;
              results.push({
                type,
                id: encodedId,
                label: buildLabel(type, item),
                to: buildTo(type, encodedId),
              });
              return;
            }

            const id = safeId(item);
            if (!id) return;

            // If searching jobs, prefer loadNumber matches (users won't know mongo _id)
            if (type === "job" && mode !== "recent" && term) {
              const loadNumber = String(item?.loadNumber || "");
              if (!loadNumber.toLowerCase().includes(term.toLowerCase())) return;
            }

            results.push({
              type,
              id,
              label: buildLabel(type, item),
              to: buildTo(type, id),
            });
          });
        });

        return { data: { results } };
      },
    }),
  }),
  overrideExisting: false,
});

export const { useLazySearchChatReferencesQuery, useSearchChatReferencesQuery } =
  chatReferencesApi;

